import React, { memo } from 'react';

import { AccordionSummary, Box, Stack, Typography } from '@mui/material';
import BadgeCDS, {
  BadgeColorVariant,
  BadgeType,
} from 'app/shared/componentsRestyled/coreDesignSystem/BadgeCDS';
import { ArrowCheckIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import { LabelSize } from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import PropTypes from 'prop-types';

import { ActionStepStatus, ActionStepType } from 'constants/aiChat';
import { IconSize } from 'constants/coreDesignSystem';

import { AiChatTestIds } from '../../../../AiChat.testIds';
import {
  ActionStepTypeIconMap,
  ActionStepTypeLabelMap,
  ActionStepTypeLabelVariantMap,
} from '../../../ActionStep/ActionStep.constants';
import { isActionStepStatusActive } from '../../../ActionStep/ActionStep.utils';

import {
  accordionSummaryStyles,
  statusLineStyles,
  summaryContentWrapperStyles,
  titleStyles,
  typeIconWrapperStyles,
} from './ActionStepGroupSummary.styles';
import ElapsedTimeLabel from './ElapsedTimeLabel';

const ActionStepGroupSummary = ({
  label,
  status,
  actionStepType = null,
  startTime = null,
  description = null,
  progressText = null,
}) => {
  const isActive = isActionStepStatusActive(status);
  const TypeIcon = ActionStepTypeIconMap[actionStepType];
  const typeLabel = ActionStepTypeLabelMap[actionStepType];
  const typeLabelVariant = ActionStepTypeLabelVariantMap[actionStepType];

  return (
    <AccordionSummary
      data-testid={AiChatTestIds.ACTION_STEP_GROUP_SUMMARY_ACCORDION}
      expandIcon={<ArrowCheckIcon size={IconSize[16]} />}
      sx={accordionSummaryStyles}
    >
      <Stack sx={summaryContentWrapperStyles}>
        <Box
          data-testid={AiChatTestIds.ACTION_STEP_GROUP_SUMMARY_STATUS_LINE}
          sx={statusLineStyles}
        >
          <Box data-testid={AiChatTestIds.ACTION_STEP_GROUP_SUMMARY_TITLE} sx={titleStyles}>
            {isActive && <BadgeCDS colorVariant={BadgeColorVariant.BRAND} type={BadgeType.DOT} />}
            {TypeIcon && (
              <Box sx={typeIconWrapperStyles}>
                <TypeIcon size={IconSize[16]} />
              </Box>
            )}
            {typeLabel && (
              <LabelCDS
                withBorder
                size={LabelSize.S}
                title={<Typography variant="regular12">{typeLabel.toLocaleUpperCase()}</Typography>}
                variant={typeLabelVariant}
              />
            )}
            <Typography variant="medium14">{label}</Typography>
          </Box>
          {startTime && <ElapsedTimeLabel startTime={startTime} />}
        </Box>

        {isActive && progressText && (
          <Typography
            data-testid={AiChatTestIds.ACTION_STEP_GROUP_SUMMARY_PROGRESS_TEXT}
            variant="regular12"
          >
            {progressText}
          </Typography>
        )}

        {!isActive && description && (
          <Typography
            data-testid={AiChatTestIds.ACTION_STEP_GROUP_SUMMARY_DESCRIPTION}
            variant="regular12"
          >
            {description}
          </Typography>
        )}
      </Stack>
    </AccordionSummary>
  );
};

ActionStepGroupSummary.propTypes = {
  label: PropTypes.string.isRequired,
  status: PropTypes.oneOf(Object.values(ActionStepStatus)).isRequired,
  actionStepType: PropTypes.oneOf(Object.values(ActionStepType)),
  startTime: PropTypes.string,
  description: PropTypes.string,
  progressText: PropTypes.string,
};

export default memo(ActionStepGroupSummary);
